import { formatMoneyWithoutTrailingZeros } from './modules/format-money-without-trailing-zeros.js';

if (!customElements.get('product-overview-frame-preview')) {
  class ProductOverviewFramePreview extends HTMLElement {
    constructor() {
      super();

      this.body = document.querySelector('body');
      this.productCardsWrapper = this.body.querySelector('[data-main-product-overview]');
      this.isCanvasCollection = this.hasAttribute('data-is-canvas-collection');

      // get all the frame/match artwork options, product card wrapper & match artwork switch state
      this.frameOptions = this.querySelectorAll('[data-frame-selector]');
      this.matchArtworkSwitches = this.querySelectorAll('[data-match-artwork-switch]');

      // check if there're promo blocks with frame options and/or match artwork to add to the array
      this.framePromoOptions = this.productCardsWrapper?.querySelectorAll(
        '[data-promo-frame-selector]'
      );
      this.matchArtworkPromoOptions = this.productCardsWrapper?.querySelectorAll(
        '[data-promo-match-artwork-switch]'
      );

      if (this.framePromoOptions.length > 0)
        this.frameOptions = [...this.frameOptions, ...this.framePromoOptions];
      if (this.matchArtworkPromoOptions.length > 0)
        this.matchArtworkSwitches = [
          ...this.matchArtworkSwitches,
          ...this.matchArtworkPromoOptions,
        ];

      // get the default frame from the theme settings
      this.currentFrame = this.querySelector('[data-frame-default]')?.dataset?.frameSelector;

      // the match artwork (preferred frames) is enabled on load, even if there're no match artwork switches
      this.isMatchArtworkEnabled = true;

      // show the preferred frames on load
      this.init();

      // handle click on the frame options
      this.frameOptions.forEach((frameOption) => {
        frameOption.addEventListener('click', () => {
          this.handleFrameSelectorChange(frameOption);
        });
      });

      // handle click on the match artwork switch (same handler reference, so it's never added twice to the same switch)
      this.onMatchArtworkClick = (e) => this.handleMatchArtworkClick(e.currentTarget);
      this.matchArtworkSwitches.forEach((matchSwitch) => {
        matchSwitch.addEventListener('click', this.onMatchArtworkClick);
      });
    }

    /**
     * init
     * @description Check the match artwork switches and show the preferred frame (or the default frame) on the product cards
     */
    init() {
      // check all switches, in case the browser restored a previous state
      this.matchArtworkSwitches.forEach((matchSwitch) => (matchSwitch.checked = true));

      // update the product cards with the preferred frame for each product
      this.updateProductCardsWithSelectedFrame();

      // display the frames on the product cards after the status is updated (to avoid flashing the default)
      this.productCardsWrapper?.classList.remove('product-cards-wrapper--hide-frames');
    }

    /**
     * handleFrameSelectorChange
     * @description Handle the change of the frame options selection
     */
    handleFrameSelectorChange(selectedFrame) {
      // update the variable with the selected frame
      this.currentFrame = selectedFrame.dataset.frameSelector;

      // disable the match artwork switch when a frame is selected
      this.isMatchArtworkEnabled = false;
      this.matchArtworkSwitches.forEach((matchSwitch) => {
        matchSwitch.checked = false;
      });

      // update active class on the frame option buttons
      this.frameOptions.forEach((frame) => {
        frame.classList.toggle(
          'frame-option--active',
          selectedFrame.dataset.frameSelector == frame.dataset.frameSelector
        );
      });

      // update the product cards with the selected frame
      this.updateProductCardsWithSelectedFrame();

      // close drawer on frame click if the user is on a smaller screen
      if (window.innerWidth < 768) this.body.classList.remove('frames-is-open');
    }

    /**
     * updateProductCardsWithSelectedFrame
     * @description Overlay the product cards with the selected frame image
     */
    updateProductCardsWithSelectedFrame() {
      let defaultFrame, square;

      // show the preferred passe-partouts (and scale down the art prints) only when the Match artwork option is enabled
      this.productCardsWrapper?.classList.toggle(
        'product-cards-wrapper--match-artwork',
        this.isMatchArtworkEnabled
      );

      // get the 1 selected Frame when a Frame option has been selected
      if (!this.isMatchArtworkEnabled) {
        ({ defaultFrame, square } = this.getFrameImages(this.currentFrame, true));
      }

      document.querySelectorAll('.product-card__image--frame').forEach((frameImage) => {
        // get the preferred Frame for each product card when the Match artwork option is enabled
        if (this.isMatchArtworkEnabled) {
          const preferredFrameElement = frameImage.closest('[data-preferred-frame]');
          const { preferredFrame, preferredFrameImageOverlays } = preferredFrameElement.dataset;

          // the preferred frame product (metafield override) carries its own overlays, as it may not be in the Frames drawer
          if (preferredFrameImageOverlays) {
            ({ defaultFrame, square } = JSON.parse(preferredFrameImageOverlays));
          } else {
            // if there's no preferred frame for the product, show the default one
            const preferredFrameHandle =
              preferredFrame || this.querySelector('[data-frame-default]')?.dataset?.frameSelector;

            ({ defaultFrame, square } = this.getFrameImages(preferredFrameHandle));
          }
        }

        // pick the image according to the product card format
        const productCardImage = frameImage.closest('.product-card__image');
        const { productFormat } = productCardImage.dataset;
        const frameImageUrl = productFormat == 'square' ? square : defaultFrame;
        const shadowImage = productCardImage.querySelector('.product-card__image--shadow');

        // if there's no frame image available for the product format, hide the frame image and show the shadow
        frameImage.style.opacity = frameImageUrl == '' ? '0' : '1';
        if (shadowImage) shadowImage.style.opacity = frameImageUrl == '' ? '1' : '0';

        if (!frameImageUrl) return;

        // update the frame image
        frameImage.srcset = frameImageUrl;
      });

      // remove the parent class for no frames when Frames exist
      if (defaultFrame != '' || square != '') {
        this.productCardsWrapper?.classList.remove('product-cards-wrapper--no-frame');
      }

      this.updateCanvasProductCards();
    }

    /**
     * updateCanvasProductCards
     * @description Update each canvas product card's link (with the current Canvas frame) and
     * price (product price plus the frame that card is showing) in a single grid pass
     */
    updateCanvasProductCards() {
      if (!this.isCanvasCollection) return;

      const productOverviewGrid = document.querySelector('[data-product-overview-grid]');
      if (!productOverviewGrid) return;

      productOverviewGrid.querySelectorAll('[data-product-card]').forEach((card) => {
        const preferredFrameElement = card.querySelector('[data-preferred-frame]');
        const frameHandle = this.isMatchArtworkEnabled
          ? preferredFrameElement?.dataset.preferredFrame ||
            this.querySelector('[data-frame-default]')?.dataset?.frameSelector
          : this.currentFrame;

        // a regular preferred frame product only changes the overlay, without the Canvas link params and frame price
        const isRegularPreferredFrame =
          this.isMatchArtworkEnabled &&
          preferredFrameElement?.hasAttribute('data-preferred-frame-not-canvas');

        card.querySelectorAll('[data-product-link][href]').forEach((link) => {
          const url = new URL(link.href, window.location.origin);

          if (isRegularPreferredFrame) {
            url.searchParams.delete('framing');
            url.searchParams.delete('canvas_frame');
          } else {
            url.searchParams.set('framing', 'canvas');
            if (frameHandle) url.searchParams.set('canvas_frame', frameHandle);
          }

          const nextUrl = `${url.pathname}${url.search}${url.hash}`;
          if (link.getAttribute('href') !== nextUrl) link.setAttribute('href', nextUrl);
        });

        const priceEl = card.querySelector('[data-product-price]');
        if (!priceEl) return;

        const { framePrice, frameComparePrice } = isRegularPreferredFrame
          ? { framePrice: 0, frameComparePrice: 0 }
          : this.getFramePrices(
              frameHandle,
              this.isMatchArtworkEnabled ? preferredFrameElement : null
            );
        const productPrice = Number(priceEl.dataset.productPrice) || 0;
        const comparePrice = Number(priceEl.dataset.productComparePrice) || 0;

        this.setPriceAmounts(priceEl, productPrice, framePrice, comparePrice, frameComparePrice);
      });
    }

    /**
     * getFramePrices
     * @description Get the canvas frame selling and compare-at prices in cents from the preferred frame product or the frame drawer
     * @param {string} frameHandle
     * @param {HTMLElement|null} preferredFrameElement - Card element with the preferred frame product prices, when Match artwork is on
     * @returns {{ framePrice: number, frameComparePrice: number }}
     */
    getFramePrices(frameHandle, preferredFrameElement = null) {
      // the preferred frame product carries its own prices, as it may not be in the Frames drawer
      const { preferredFramePrice, preferredFrameComparePrice } =
        preferredFrameElement?.dataset ?? {};
      if (preferredFramePrice) {
        const framePrice = Number(preferredFramePrice) || 0;
        return { framePrice, frameComparePrice: Number(preferredFrameComparePrice) || framePrice };
      }

      const selectedFrame =
        this.querySelector(`[data-frame-main-selectors] [data-frame-selector="${frameHandle}"]`) ||
        this.querySelector('[data-frame-main-selectors] [data-frame-default]');
      const framePrice = Number(selectedFrame?.dataset.framePrice) || 0;
      const frameComparePrice = Number(selectedFrame?.dataset.frameComparePrice) || framePrice;

      return { framePrice, frameComparePrice };
    }

    /**
     * setPriceAmounts
     * @description Same totals as format-price, using the frame the card is showing
     */
    setPriceAmounts(priceEl, productPrice, framePrice, comparePrice, frameComparePrice) {
      const productHasCompare = comparePrice > productPrice;
      const showCompare = productHasCompare || frameComparePrice > framePrice;
      const salePrice = productPrice + framePrice;
      const compareTotal = showCompare
        ? (productHasCompare ? comparePrice : productPrice) + frameComparePrice
        : 0;

      const formattedPrice = formatMoneyWithoutTrailingZeros(salePrice);
      const formattedCompare = formatMoneyWithoutTrailingZeros(compareTotal);
      const compareEl = priceEl.querySelector('[data-product-card-compare-price]');
      const amountEls = priceEl.querySelectorAll('[data-price-amount]');

      compareEl?.classList.toggle('hidden', !showCompare);

      if (amountEls.length > 0) {
        amountEls.forEach((amountEl) => {
          const isCompare = amountEl.closest('[data-product-card-compare-price]');
          amountEl.textContent = isCompare ? formattedCompare : formattedPrice;
        });
        return;
      }

      const fromEl = priceEl.querySelector('.price__item:not([data-product-card-compare-price])');
      if (!fromEl) return;

      const previousPrice = formatMoneyWithoutTrailingZeros(productPrice);
      fromEl.textContent = fromEl.textContent.replace(previousPrice, formattedPrice);
    }

    /**
     * getFrameImages
     * @description Get the selected/preferred frame images for the product cards
     * @param {string} frameHandle
     * @param {boolean} allProductCards
     */
    getFrameImages(frameHandle, allProductCards = false) {
      // get the selected frame from the drawer with all the Frame options
      let selectedFrame = this.querySelector(
        `[data-frame-main-selectors] [data-frame-selector="${frameHandle}"]`
      );

      // set fallback to the default frame, in case the frame is not found in the frames drawer
      if (selectedFrame == null) {
        frameHandle = this.querySelector('[data-frame-default]')?.dataset?.frameSelector;
        selectedFrame = this.querySelector(
          `[data-frame-main-selectors] [data-frame-selector="${frameHandle}"]`
        );
      }

      // hide the frame images when the selected option has no frame overlays, like the 'None' option (only for general frame changes, not match artwork)
      if (allProductCards && !selectedFrame?.dataset.frameImageOverlays) {
        // hide frame img when the option 'None' is selected
        this.productCardsWrapper?.classList.add('product-cards-wrapper--no-frame');
        return { defaultFrame: '', square: '' };
      }

      // safety net: no frame overlays when neither the frame nor the default frame are in the drawer (misconfigured theme settings), so the cards show the shadow instead of breaking the frame preview
      if (!selectedFrame?.dataset.frameImageOverlays) return { defaultFrame: '', square: '' };

      // get the overlay images for the different formats
      const overlayImage = JSON.parse(selectedFrame.dataset.frameImageOverlays);
      const { defaultFrame, square } = overlayImage;

      return { defaultFrame, square };
    }

    /**
     * updateMatchArtworkPromoSwitches
     * @description Update the promo match artwork switches after the product grid is re-rendered (filtering, sorting or pagination), sync their state and add the click event
     */
    updateMatchArtworkPromoSwitches() {
      this.matchArtworkPromoOptions = this.productCardsWrapper?.querySelectorAll(
        '[data-promo-match-artwork-switch]'
      );

      this.matchArtworkSwitches = [
        ...this.querySelectorAll('[data-match-artwork-switch]'),
        ...this.matchArtworkPromoOptions,
      ];

      this.matchArtworkPromoOptions.forEach((matchSwitch) => {
        // sync the switch with the current match artwork state, since it's rendered checked by default
        matchSwitch.checked = this.isMatchArtworkEnabled;

        matchSwitch.addEventListener('click', this.onMatchArtworkClick);
      });
    }

    /**
     * handleMatchArtworkClick
     * @description Update the Match artwork switches state and update the Product cards according to the preferred Frame or previously selected frame
     * @param {object} clickedMatchSwitch
     */
    handleMatchArtworkClick(clickedMatchSwitch) {
      // get the current state of the clicked match artwork switch
      this.isMatchArtworkEnabled = clickedMatchSwitch.checked;

      // update all match artwork switches with the current state
      this.matchArtworkSwitches.forEach((matchSwitch) => {
        matchSwitch.checked = this.isMatchArtworkEnabled;
      });

      if (this.isMatchArtworkEnabled) {
        // switch view to Product when the Match Artwork is enabled
        this.cardOverviewView = document.querySelector('card-overview-view');
        this.cardOverviewView?.switchView(this.cardOverviewView.viewPrimary);

        // deselect the frame options when the match artwork switch is enabled
        this.frameOptions.forEach((frameOption) => {
          frameOption.classList.remove('frame-option--active');
        });

        // update the product cards with the preferred frame for each product
        this.updateProductCardsWithSelectedFrame();
      } else {
        // change the frame selection based on the last selected frame or the default
        this.handleFrameSelectorChange(
          this.querySelector(
            `[data-frame-main-selectors] [data-frame-selector="${this.currentFrame}"]`
          )
        );
      }

      // close drawer on match artwork click if the user is on a smaller screen
      if (clickedMatchSwitch.id == 'drawer-match-artwork' && window.innerWidth < 768) {
        const closeDrawer = (e) => {
          if (e.propertyName !== 'transform') return;
          this.body.classList.remove('frames-is-open');
          clickedMatchSwitch.nextElementSibling.removeEventListener('transitionend', closeDrawer);
        };

        clickedMatchSwitch.nextElementSibling.addEventListener('transitionend', closeDrawer);
      }
    }
  }
  customElements.define('product-overview-frame-preview', ProductOverviewFramePreview);
}
